import { useEffect, useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';

// Our components
import Alert from 'components/Alert/index';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import PropertyTypeDropdown from 'components/Dropdown/mortgage/PropertyTypeDropdown';
import SoraTextField from 'components/Input/SoraTextField';
import { NUMBER } from 'components/Input/Types';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';
import TextInput from 'components/Input/TextInput';
import { PrimaryButton } from 'components/Button/Button';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Utils
import {
	clearFormValues,
	isSubmissionReady,
	dollarFormatter
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	TYPES_OF_HOME_PROPERTIES,
	MORTGAGE,
	NEW_LOAN_OFFER_TYPE,
	MORTGAGE_LOAN_TENURE_OPTIONS
} from 'shared/constants';

function NewMortgageForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	loanType,
	loanRequestId,
	financialData,
	postSuccessEvent,
	disableAutoComplete,
	setDisableAutoComplete
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Build full name
	const {
		clientFullName,
		clientId,
		firstName,
		lastName,
		annualIncome,
		creditScoreValue
	} = clientInfo;

	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	const [propertyType, setPropertyType] = useState('');
	const [estimatedPurchaseValue, setEstimatedPurchaseValue] = useState('');
	const [primaryUse, setPrimaryUse] = useState('');
	const [downPaymentPercent, setDownPaymentPercent] = useState('');
	const [firstTimeHomeBuyer, setFirstTimeHomeBuyer] = useState('Yes');
	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	// Address Related
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const formData = [
		estimatedPurchaseValue,
		loanAmount,
		ownershipTenure,
		city,
		state,
		zipCode
	];

	const loanRequestDetailsRequiredFields = [
		propertyType,
		primaryUse,
		annualIncome,
		downPaymentPercent,
		estimatedPurchaseValue,
		loanAmount,
		ownershipTenure,
		city,
		state,
		zipCode
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl
			? loanRequestDetailsRequiredFields
			: [...formData, isClientNameProvided]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `New Mortgage Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Client's down payment: ${downPaymentPercent}%`,
				`Client's expected ownership tenure: ${ownershipTenure}`,
				`Client's primary use for property: ${primaryUse}`,
				`Client's entered property type: ${propertyType}`,
				`Client's estimated purchase value: ${dollarFormatter.format(
					+estimatedPurchaseValue
				)}`,
				`PropertyAddress: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`,
				`First Time Home Buyer: ${firstTimeHomeBuyer}`
			],
			additionalNotes,
			lendingTypeRequested: 'New Mortgage Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setLoanAmount,
						setAddressLine1,
						setAddressLine2,
						setPrimaryUse,
						setEstimatedPurchaseValue,
						setDownPaymentPercent,
						setOwnershipTenure,
						setPropertyType,
						setFirstTimeHomeBuyer,
						setAdditionalNotes,
						setCity,
						setState,
						setZipCode
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScoreValue,
			downPaymentPercent,
			estimatedPurchaseValue,
			firstName,
			firstTimeHomeBuyer,
			lastName,
			loanAmount,
			offerType: NEW_LOAN_OFFER_TYPE,
			ownershipTenure,
			primaryUse,
			propertyType,
			state,
			annualIncome,
			tradelineType: MORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScoreValue,
			downPaymentPercent,
			estimatedPurchaseValue,
			firstName,
			firstTimeHomeBuyer,
			lastName,
			loanAmount,
			offerType: NEW_LOAN_OFFER_TYPE,
			ownershipTenure,
			primaryUse,
			propertyType,
			state,
			annualIncome,
			tradelineType: MORTGAGE,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setDownPaymentPercent,
						setEstimatedPurchaseValue,
						setFirstTimeHomeBuyer,
						setLoanAmount,
						setOwnershipTenure,
						setPrimaryUse,
						setPropertyType,
						setState,
						setZipCode
					]);

					if (postSuccessEvent) {
						postSuccessEvent();
					}
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: estimatedPurchaseValue,
				label: 'Estimated Purchase Value:'
			},
			{
				type: 'percent',
				value: downPaymentPercent,
				label: 'Down Payment:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired Loan Amount:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				First Time Home Buyer: ${firstTimeHomeBuyer}
				${formattedCollection}
				Property Type: ${propertyType}
				Primary Use: ${primaryUse}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest(loanRequestId);
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						New Mortgage Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Type of property input */}
						<PropertyTypeDropdown
							selected={propertyType}
							onChange={setPropertyType}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Primary use of property */}
						<Dropdown
							items={TYPES_OF_HOME_PROPERTIES}
							selected={primaryUse}
							onChange={setPrimaryUse}
							variant="outlined"
							label={
								atLoanRequestDetailsUrl
									? 'Property use *'
									: 'Property use'
							}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* First time home owner */}
						<Dropdown
							items={['Yes', 'No']}
							selected={firstTimeHomeBuyer}
							onChange={setFirstTimeHomeBuyer}
							variant="outlined"
							label="First time home buyer?"
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Estimated Purchase Value */}
						<CurrencyTextInput
							label="Estimated Purchase Value *"
							subLabel="Estimated price of home"
							value={estimatedPurchaseValue}
							onChange={setEstimatedPurchaseValue}
							inputProps={{
								'data-test': 'estimatedPurchaseValue'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Down payment % */}
						<TextInput
							type="percent"
							label={
								atLoanRequestDetailsUrl
									? 'Down payment % *'
									: 'Down payment %'
							}
							helperText="Down payment cannot be empty"
							onChange={setDownPaymentPercent}
							value={downPaymentPercent}
							inputProps={{
								'data-test': 'downPaymentPercent'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Desired Loan Amount */}
						<CurrencyTextInput
							label="Desired loan amount *"
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Loan Tenure */}
						<Dropdown
							items={MORTGAGE_LOAN_TENURE_OPTIONS}
							selected={ownershipTenure}
							onChange={setOwnershipTenure}
							variant="outlined"
							label="Desired loan term *"
						/>
					</Grid>

					<GetFullAddressPartialForm
						isRequired
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
						disableAutoComplete={disableAutoComplete}
						setDisableAutoComplete={setDisableAutoComplete}
					/>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

export default NewMortgageForm;
